/**
 * Exportación SUENLACE A3 para FACTURAS EMITIDAS (ventas).
 *
 * Reutiliza la misma especificación posicional (v9.80, 512 bytes/registro, CRLF,
 * Win-1252) que el módulo de recibidas (`a3ecoExport.ts`), adaptando los
 * discriminadores propios de ventas:
 *
 *   - Reg "C" : alta de la ficha del CLIENTE (cuenta 430xxxxx).
 *   - Reg "1" / "2" : cabecera de la factura emitida.
 *       · pos 58 = "1" (Ventas)        ← diferencia clave vs compras "2"
 *       · pos 15 = "2" para abonos / rectificativas
 *   - Reg "9" : detalle IVA repercutido.
 *       · pos 58 = "A" (Abono) → la cuenta 700 va al Haber.
 *       · pos 154-172 IRPF se interpreta como retención SOPORTADA por el cliente.
 *   - Reg "V" : vencimiento del cobro.
 *       · pos 58 = "C" (Cobro)         ← vs "P" en recibidas
 *
 * Pensado para volcar Excels de ventas que el cliente exporta de su ERP, sin
 * leasing, sin auto-repercusión (intracom/ISP en ventas se manejará en una
 * iteración posterior cuando aparezcan casos reales en producción).
 */

import { parseNumericLike } from "@/lib/numberFormatting";

const LINE_LENGTH = 512;
const PAYLOAD_LENGTH = 510;
const FORMAT_TAG = "5";
const CURRENCY_TAG = "E";
const GENERATED_TAG = "N";
const WIN1252_FALLBACK = 0x3f;

const padLeft = (v: string, w: number, c = "0") =>
  v.length >= w ? v.slice(-w) : c.repeat(w - v.length) + v;
const padRight = (v: string, w: number, c = " ") =>
  v.length >= w ? v.slice(0, w) : v + c.repeat(w - v.length);
const cleanText = (s: string | null | undefined) =>
  (s ?? "").replace(/[\r\n\t]/g, " ").replace(/\s+/g, " ").trim();

const padAccount12 = (s: string | null | undefined): string => {
  const digits = (s ?? "").replace(/[^0-9]/g, "");
  if (!digits) return padRight("", 12);
  const acc = digits.length > 12 ? digits.slice(0, 12) : digits;
  return padRight(acc, 12);
};

const buildVatAccount = (root: string, referenceAccounts: (string | null | undefined)[]): string => {
  const lengths = referenceAccounts
    .map((a) => (a ?? "").replace(/[^0-9]/g, "").length)
    .filter((n) => n >= 6 && n <= 12);
  const targetLen = lengths.length > 0 ? Math.max(...lengths) : 8;
  return root.padEnd(targetLen, "0");
};

const formatA3Amount = (amount: number): string => {
  const sign = amount < 0 ? "-" : "+";
  const abs = Math.abs(amount);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  if (decPart >= 100) return sign + padLeft(String(intPart + 1), 10) + "." + "00";
  return sign + padLeft(String(intPart), 10) + "." + padLeft(String(decPart), 2);
};

const formatA3Rate = (rate: number): string => {
  const abs = Math.abs(rate);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  if (decPart >= 100) return padLeft(String(intPart + 1), 2) + "." + "00";
  return padLeft(String(intPart), 2) + "." + padLeft(String(decPart), 2);
};

const formatA3Date = (iso: string): string => {
  if (!iso) return "        ";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  const m2 = iso.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m2) return `${m2[3]}${m2[2]}${m2[1]}`;
  return "        ";
};

const A3_COUNTRY_NUMERIC: Record<string, string> = {
  ES: "011", FR: "109", DE: "067", IT: "103", PT: "165", IE: "153",
  NL: "137", BE: "017", AT: "002", LU: "129", DK: "057", SE: "203",
  FI: "077", PL: "163", CZ: "055", SK: "189", HU: "099", RO: "175",
  GR: "087", EL: "087", BG: "043", HR: "053", SI: "187", EE: "071",
  LV: "117", LT: "121", MT: "133", CY: "049", GB: "069", XI: "069",
  CH: "047", NO: "143", US: "207", MX: "131",
};

const detectForeignPrefix = (raw: string | null | undefined) => {
  const id = (raw ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (id.length < 4) return null;
  const prefix = id.slice(0, 2);
  if (prefix === "ES") return null;
  if (!/^[A-Z]{2}$/.test(prefix)) return null;
  if (!A3_COUNTRY_NUMERIC[prefix]) return null;
  return { country: prefix, rest: id.slice(2) };
};

const cleanTaxId = (taxId: string | null | undefined): string => {
  const raw = cleanText(taxId).toUpperCase().replace(/[\s.\-/]/g, "");
  if (raw.startsWith("ES") && raw.length === 11) return raw.slice(2);
  return raw;
};

/** Número completo de factura: serie_número (p. ej. serie "1" + "260001" → "1_260001"). */
export const fullInvoiceNumber = (invoiceNumber: string, series?: string): string => {
  const s = cleanText(series ?? "").toUpperCase();
  const inv = cleanText(invoiceNumber).toUpperCase();
  if (inv && s && !inv.startsWith(`${s}_`)) return `${s}_${inv}`;
  return inv || s;
};

const buildDocumentNumber = (invoiceNumber: string, series?: string): string => {
  const doc = fullInvoiceNumber(invoiceNumber, series);
  return doc ? padRight(doc.slice(0, 10), 10) : padRight("", 10);
};

const invoiceConcept = (invoiceNumber: string): string => {
  const inv = cleanText(invoiceNumber).toUpperCase();
  return `N/Fra. ${inv}`.slice(0, 30);
};


const encodeWin1252 = (text: string): Uint8Array => {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes.push(code);
    else if (code >= 0xa0 && code <= 0xff) bytes.push(code);
    else {
      switch (code) {
        case 0x20ac: bytes.push(0x80); break;
        case 0x201a: bytes.push(0x82); break;
        case 0x0192: bytes.push(0x83); break;
        case 0x201e: bytes.push(0x84); break;
        case 0x2026: bytes.push(0x85); break;
        case 0x2020: bytes.push(0x86); break;
        case 0x2021: bytes.push(0x87); break;
        case 0x02c6: bytes.push(0x88); break;
        case 0x2030: bytes.push(0x89); break;
        case 0x0160: bytes.push(0x8a); break;
        case 0x2018: bytes.push(0x91); break;
        case 0x2019: bytes.push(0x92); break;
        case 0x201c: bytes.push(0x93); break;
        case 0x201d: bytes.push(0x94); break;
        case 0x2022: bytes.push(0x95); break;
        case 0x2013: bytes.push(0x96); break;
        case 0x2014: bytes.push(0x97); break;
        default: bytes.push(WIN1252_FALLBACK);
      }
    }
  }
  return new Uint8Array(bytes);
};

const buildLine = (fields: { pos: number; value: string }[]): string => {
  const buf = new Array(PAYLOAD_LENGTH).fill(" ");
  buf[0] = FORMAT_TAG;
  buf[508] = CURRENCY_TAG;
  buf[509] = GENERATED_TAG;
  for (const f of fields) {
    const idx = f.pos - 1;
    for (let i = 0; i < f.value.length && idx + i < PAYLOAD_LENGTH; i++) {
      buf[idx + i] = f.value[i];
    }
  }
  return buf.join("");
};

// ── Tipos públicos ────────────────────────────────────────────────────────

export interface IssuedInvoiceRow {
  /** Fecha del asiento contable / fecha de factura (ISO YYYY-MM-DD). */
  date: string;
  /** Serie del ERP (opcional) — se vuelca en el nº de documento (10 chars). */
  series?: string;
  invoiceNumber: string;
  customerName: string;
  customerTaxId: string;
  customerAccount: string;
  salesAccount: string;
  /** Cada tramo de IVA (puede haber 1..n). */
  vatLines: {
    taxableBase: number;
    vatRate: number;
    vatAmount: number;
    /** % Recargo Equivalencia asociado a este tramo (opcional). */
    reRate?: number;
    /** Cuota Recargo Equivalencia asociada a este tramo (opcional). */
    reAmount?: number;
  }[];
  irpfRate?: number | null;
  irpfAmount?: number | null;
  totalAmount: number;
  postalCode?: string;
  /** Forma de cobro tal como viene del ERP ("CONTADO", "GIRO", "TARJETA"…). */
  paymentMethod?: string;
}

export interface IssuedExportOptions {
  /** Código de empresa A3 (5 dígitos, se rellena con ceros a la izquierda). */
  companyCode: string;
  /** Mapa forma de pago (UPPER sin acentos) → cuenta de tesorería (570/571…). */
  paymentAccountMap?: Record<string, string>;
  /** Si false, no genera el asiento de cobro aunque haya mapa. Default: true. */
  generatePaymentEntry?: boolean;
}

export interface IssuedBuildResult {
  bytes: Uint8Array;
  fileName: string;
  invoiceCount: number;
  lineCount: number;
  warnings: string[];
}

export const getIssuedVatAccount = (row: Pick<IssuedInvoiceRow, "salesAccount" | "customerAccount" | "vatLines">): string => {
  const hasVat = (row.vatLines || []).some(
    (line) => Math.abs(parseNumericLike(line.vatAmount)) > 0 || Math.abs(parseNumericLike(line.vatRate)) > 0,
  );
  if (!hasVat) return "";
  return buildVatAccount("477", [row.salesAccount, row.customerAccount]);
};

// ── Constructores de registro ─────────────────────────────────────────────

/**
 * Cuentas de cliente genérico (facturas simplificadas / ventas a contado):
 * se admiten sin CIF y no generan registro de alta de cuenta en A3.
 */
export const GENERIC_CUSTOMER_ACCOUNTS = new Set(["43000000", "430000000000"]);

export const isGenericCustomerAccount = (account: string | null | undefined): boolean =>
  GENERIC_CUSTOMER_ACCOUNTS.has(cleanText(account).replace(/\D/g, ""));

const buildCustomerAccountRecord = (
  empresa: string,
  date: string,
  customerAccount: string,
  customerName: string,
  taxId: string,
  postalCode: string,
): string => {
  const foreign = detectForeignPrefix(taxId);
  const country = foreign ? A3_COUNTRY_NUMERIC[foreign.country] : "011";
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: "C" },
    { pos: 16, value: padAccount12(customerAccount) },
    { pos: 28, value: padRight(cleanText(customerName).toUpperCase(), 30) },
    { pos: 58, value: "N" },
    { pos: 59, value: formatA3Amount(0) },
    { pos: 73, value: " " },
    { pos: 78, value: padRight(cleanTaxId(taxId), 14) },
    { pos: 175, value: country },
  ];
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) fields.push({ pos: 155, value: padLeft(cp.slice(0, 5), 5) });
  return buildLine(fields);
};

const buildIssuedInvoiceHeader = (
  empresa: string,
  date: string,
  isRectificativa: boolean,
  customerAccount: string,
  customerName: string,
  documentNumber: string,
  totalAmount: number,
  taxId: string,
  concept: string,
  invoiceNumber: string,
  postalCode: string,
): string => {
  const cleaned = cleanTaxId(taxId);
  const foreign = detectForeignPrefix(taxId);
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: isRectificativa ? "2" : "1" },
    { pos: 16, value: padAccount12(customerAccount) },
    { pos: 28, value: padRight(cleanText(customerName).toUpperCase(), 30) },
    { pos: 58, value: "1" }, // Ventas
    { pos: 59, value: documentNumber },
    { pos: 69, value: "I" },
    { pos: 70, value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 176, value: padRight(cleaned, 14) },
    { pos: 190, value: padRight(cleanText(customerName).toUpperCase(), 40) },
    { pos: 245, value: formatA3Date(date) },
    { pos: 253, value: padRight(cleanText(invoiceNumber).toUpperCase(), 60) },
    { pos: 374, value: foreign ? foreign.country : "  " },
  ];
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) fields.push({ pos: 230, value: padLeft(cp.slice(0, 5), 5) });
  return buildLine(fields);
};

const buildIssuedVatDetail = (
  empresa: string,
  date: string,
  customerAccount: string,
  salesAccount: string,
  documentNumber: string,
  concept: string,
  marker: "M" | "U",
  base: number,
  rate: number,
  cuota: number,
  irpfRate: number,
  irpfAmount: number,
  reRate: number,
  reAmount: number,
): string => {
  const vatAccount = Math.abs(cuota) > 0 || Math.abs(rate) > 0
    ? buildVatAccount("477", [salesAccount, customerAccount])
    : "";
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: "9" },
    { pos: 16, value: padAccount12(salesAccount) },
    { pos: 28, value: padRight("VENTAS", 30) },
    { pos: 58, value: "C" },
    { pos: 59, value: documentNumber },
    { pos: 69, value: marker },
    { pos: 70, value: padRight(concept, 30) },
    { pos: 100, value: "01" },
    { pos: 102, value: formatA3Amount(base) },
    { pos: 116, value: formatA3Rate(rate) },
    { pos: 121, value: formatA3Amount(cuota) },
    { pos: 135, value: formatA3Rate(reRate) },
    { pos: 140, value: formatA3Amount(reAmount) },
    { pos: 154, value: formatA3Rate(irpfRate) },
    { pos: 159, value: formatA3Amount(irpfAmount) },
    { pos: 173, value: irpfAmount > 0 || irpfRate > 0 ? "05" : "01" },
    { pos: 175, value: "S" },
  ];
  if (vatAccount) fields.push({ pos: 228, value: padRight(vatAccount, 12) });
  return buildLine(fields);
};

/**
 * Registro tipo "0" para asiento manual libre. Se usa para el asiento de cobro
 * (cuenta tesorería DEBE / cuenta cliente HABER) cuando la forma de pago del
 * ERP implica un movimiento inmediato de caja (CONTADO / TARJETA / GIRO…).
 * Estructura mínima según la especificación:
 *   1       Formato (5)
 *   2-6     Empresa
 *   7-14    Fecha del apunte (aaaammdd)
 *   15      "0"
 *   16-27   Cuenta (12)
 *   28-57   Descripción de la cuenta (30)
 *   58      "D" Debe / "H" Haber
 *   59-68   Referencia documento (10)
 *   69      "I" inicio / "M" medio / "U" último
 *   70-99   Descripción del apunte (30)
 *   100-113 Importe (14)
 */
const buildSimpleEntryRecord = (
  empresa: string,
  date: string,
  account: string,
  description: string,
  side: "D" | "H",
  reference: string,
  marker: "I" | "M" | "U",
  concept: string,
  amount: number,
): string =>
  buildLine([
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "0" },
    { pos: 16,  value: padAccount12(account) },
    { pos: 28,  value: padRight(cleanText(description).toUpperCase(), 30) },
    { pos: 58,  value: side },
    { pos: 59,  value: reference },
    { pos: 69,  value: marker },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(Math.abs(amount)) },
  ]);

/** Normaliza forma de pago para lookup: mayúsculas, sin acentos, sin espacios extra. */
export const normalizePaymentMethod = (raw: string | null | undefined): string =>
  (raw || "")
    .toString()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

const buildDueDateRecord = (
  empresa: string,
  date: string,
  customerAccount: string,
  customerName: string,
  documentNumber: string,
  invoiceNumber: string,
  totalAmount: number,
): string => {
  const concept = `VTO FRA/${cleanText(invoiceNumber).toUpperCase()}`.slice(0, 30);
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: "V" },
    { pos: 16, value: padAccount12(customerAccount) },
    { pos: 28, value: padRight(cleanText(customerName).toUpperCase(), 30) },
    { pos: 58, value: "C" }, // Cobro (factura emitida)
    { pos: 59, value: documentNumber },
    { pos: 69, value: " " },
    { pos: 70, value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 114, value: formatA3Date(date) },
  ];
  return buildLine(fields);
};

// ── API pública ───────────────────────────────────────────────────────────

export const validateIssuedRows = (rows: IssuedInvoiceRow[]): string[] => {
  const warnings: string[] = [];
  for (const r of rows) {
    const id = r.invoiceNumber || "(s/n)";
    if (!r.customerTaxId && !isGenericCustomerAccount(r.customerAccount)) {
      warnings.push(`Falta NIF del cliente en factura ${id}`);
    }
    if (!r.customerAccount) warnings.push(`Falta cuenta de cliente en factura ${id} (${r.customerName})`);
    if (!r.salesAccount) warnings.push(`Falta cuenta de venta en factura ${id} (${r.customerName})`);
    if (!r.date) warnings.push(`Falta fecha en factura ${id}`);
    if (!r.vatLines || r.vatLines.length === 0) warnings.push(`Sin líneas de IVA en factura ${id}`);
    // Cuadre base + IVA + RE − IRPF = total (tolerancia 0,05 €)
    const sumBase = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
    const sumVat = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
    const sumRe = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.reAmount ?? 0), 0);
    const irpf = parseNumericLike(r.irpfAmount ?? 0);
    const expected = sumBase + sumVat + sumRe - irpf;
    if (Math.abs(expected - parseNumericLike(r.totalAmount)) > 0.05) {
      warnings.push(
        `Descuadre en factura ${id}: base+IVA+RE−IRPF = ${expected.toFixed(2)} € pero total = ${parseNumericLike(r.totalAmount).toFixed(2)} €`,
      );
    }
  }
  return warnings;
};

/**
 * Ajuste de redondeo: A3 exige que el asiento cuadre al céntimo.
 * Cuando la suma base + IVA + RE − IRPF difiere del total de la factura en
 * un importe residual (≤ 0,05 €, típico de facturas con el IVA redondeado por
 * el emisor), se absorbe la diferencia en la base del tramo de mayor importe,
 * dejando intactos el total de la factura y las cuotas de IVA.
 */
const balanceVatLines = (r: IssuedInvoiceRow): IssuedInvoiceRow["vatLines"] => {
  const lines = (r.vatLines || []).map((l) => ({ ...l }));
  if (lines.length === 0) return lines;
  const sum = (f: (l: (typeof lines)[number]) => number) => lines.reduce((s, l) => s + f(l), 0);
  const base = sum((l) => parseNumericLike(l.taxableBase));
  const vat = sum((l) => parseNumericLike(l.vatAmount));
  const re = sum((l) => parseNumericLike(l.reAmount ?? 0));
  const irpf = parseNumericLike(r.irpfAmount ?? 0);
  const total = parseNumericLike(r.totalAmount);
  const diff = Math.round((total - (base + vat + re - irpf)) * 100) / 100;
  if (diff === 0 || Math.abs(diff) > 0.05) return lines;

  let idx = 0;
  for (let i = 1; i < lines.length; i++) {
    if (Math.abs(parseNumericLike(lines[i].taxableBase)) > Math.abs(parseNumericLike(lines[idx].taxableBase))) idx = i;
  }
  lines[idx].taxableBase = Math.round((parseNumericLike(lines[idx].taxableBase) + diff) * 100) / 100;
  return lines;
};

export const buildIssuedSuenlaceFile = (
  rows: IssuedInvoiceRow[],
  options: IssuedExportOptions,
): IssuedBuildResult => {
  const empresa = padLeft((options.companyCode || "").replace(/\D/g, ""), 5);
  const warnings = validateIssuedRows(rows);
  const lines: string[] = [];

  // Deduplica clientes (un solo reg C por cuenta + NIF) en el orden de aparición.
  const seenCustomers = new Set<string>();

  const skipped: string[] = [];
  for (const r of rows) {
    if (!r.customerAccount || !r.salesAccount) {
      skipped.push(
        `Factura ${r.invoiceNumber || "(s/n)"} (${r.customerName || "s/nombre"}) omitida: ` +
          (!r.customerAccount ? "falta cuenta de cliente" : "falta cuenta de venta"),
      );
      continue;
    }
    const total = parseNumericLike(r.totalAmount);

    const isRectificativa = total < 0;
    const totalForA3 = Math.abs(total);
    const fullNumber = fullInvoiceNumber(r.invoiceNumber, r.series);
    const documentNumber = buildDocumentNumber(r.invoiceNumber, r.series);
    const concept = invoiceConcept(fullNumber);

    const customerKey = `${r.customerAccount}|${cleanTaxId(r.customerTaxId)}`;
    const genericCustomer = isGenericCustomerAccount(r.customerAccount);
    if (!genericCustomer && !seenCustomers.has(customerKey)) {
      seenCustomers.add(customerKey);
      lines.push(
        buildCustomerAccountRecord(
          empresa,
          r.date,
          r.customerAccount,
          r.customerName,
          r.customerTaxId,
          r.postalCode || "",
        ),
      );
    }

    lines.push(
      buildIssuedInvoiceHeader(
        empresa,
        r.date,
        isRectificativa,
        r.customerAccount,
        r.customerName,
        documentNumber,
        totalForA3,
        r.customerTaxId,
        concept,
        fullNumber,
        r.postalCode || "",
      ),
    );

    // Detalle IVA: una línea por tramo.
    const balanced = balanceVatLines(r);
    // Si el ajuste de redondeo ha tocado alguna base, dejamos constancia en avisos.
    for (let i = 0; i < balanced.length; i++) {
      const before = parseNumericLike(r.vatLines?.[i]?.taxableBase);
      const after = parseNumericLike(balanced[i]?.taxableBase);
      if (Math.abs(after - before) > 1e-9) {
        warnings.push(
          `Factura ${r.invoiceNumber || "(s/n)"}: base ajustada en ${(after - before).toFixed(2)} € para cuadrar el asiento (redondeo del emisor). Total e IVA intactos.`,
        );
      }
    }
    const vatLines = balanced.length > 0 ? balanced : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];
    const irpfAbs = Math.abs(parseNumericLike(r.irpfAmount ?? 0));
    const irpfRate = irpfAbs > 0 && (r.irpfRate ?? 0) > 0
      ? Math.round(Math.abs(r.irpfRate!) * 100) / 100
      : irpfAbs > 0 && vatLines.length > 0
      ? Math.round((irpfAbs / vatLines.reduce((s, l) => s + Math.abs(parseNumericLike(l.taxableBase)), 0)) * 10000) / 100
      : 0;

    for (let i = 0; i < vatLines.length; i++) {
      const v = vatLines[i];
      const isLast = i === vatLines.length - 1;
      lines.push(
        buildIssuedVatDetail(
          empresa,
          r.date,
          r.customerAccount,
          r.salesAccount,
          documentNumber,
          concept,
          isLast ? "U" : "M",
          Math.abs(parseNumericLike(v.taxableBase)),
          parseNumericLike(v.vatRate),
          Math.abs(parseNumericLike(v.vatAmount)),
          isLast ? irpfRate : 0,
          isLast ? irpfAbs : 0,
          parseNumericLike(v.reRate ?? 0),
          Math.abs(parseNumericLike(v.reAmount ?? 0)),
        ),
      );
    }

    // Asiento de cobro condicional según forma de pago (CONTADO/TARJETA/GIRO…).
    // Debe: cuenta de tesorería / Haber: cuenta del cliente (por el total factura).
    const genPayment = options.generatePaymentEntry !== false;
    const paymentMap = options.paymentAccountMap || {};
    const paymentKey = normalizePaymentMethod(r.paymentMethod);
    const treasuryAccount = paymentKey ? paymentMap[paymentKey] : undefined;
    if (genPayment && treasuryAccount && totalForA3 > 0 && !isRectificativa) {
      // Vencimiento para dejar registrado el cobro contra el cliente
      lines.push(
        buildDueDateRecord(
          empresa, r.date, r.customerAccount, r.customerName,
          documentNumber, fullNumber, totalForA3,
        ),
      );
      const payRef = documentNumber;
      const payConcept = `COBRO FRA/${fullNumber}`.slice(0, 30);
      lines.push(
        buildSimpleEntryRecord(
          empresa, r.date, treasuryAccount, `COBRO ${paymentKey}`,
          "D", payRef, "I", payConcept, totalForA3,
        ),
      );
      lines.push(
        buildSimpleEntryRecord(
          empresa, r.date, r.customerAccount, r.customerName,
          "H", payRef, "U", payConcept, totalForA3,
        ),
      );
    }
  }

  const normalized = lines.map((l, idx) => {
    if (l.length !== PAYLOAD_LENGTH) {
      throw new Error(`SUENLACE emitidas: línea ${idx + 1} tiene ${l.length} chars (esperado ${PAYLOAD_LENGTH})`);
    }
    return l;
  });

  // CRLF (0x0D 0x0A) en posiciones 511-512.
  const totalBytes = normalized.length * LINE_LENGTH;
  const buffer = new Uint8Array(totalBytes);
  for (let i = 0; i < normalized.length; i++) {
    const payload = encodeWin1252(normalized[i]);
    buffer.set(payload, i * LINE_LENGTH);
    buffer[i * LINE_LENGTH + PAYLOAD_LENGTH] = 0x0d;
    buffer[i * LINE_LENGTH + PAYLOAD_LENGTH + 1] = 0x0a;
  }

  const fileName = `SUENLACE_EMI_${empresa}_${Date.now()}.DAT`;
  return {
    bytes: buffer,
    fileName,
    invoiceCount: rows.length,
    lineCount: normalized.length,
    warnings: [...warnings, ...skipped],
  };

};
